import { BaseService } from './base'
import { APIPath } from 'utility/constant'
import { CommonUtility } from 'utility/common'
import {
    APPROVAL_STATUS,
    normalizeApprovalListResponse,
} from 'page-components/report-approval/report-approval-utils'

const approvalLookupCache = new Map()

class ReportApprovalServiceClass {
    listPendingReports(queryParams = {}) {
        const qs = CommonUtility.objectToParams(queryParams)
        const url = qs
            ? `${APIPath.approvalPendingReports}?${qs}`
            : APIPath.approvalPendingReports
        return BaseService.get(url)
    }

    getReportDetail(completedReportId) {
        return BaseService.get(
            APIPath.approvalReportDetail(completedReportId),
        )
    }

    approveReport(completedReportId, payload) {
        return BaseService.postExact(
            APIPath.approvalReportApprove(completedReportId),
            payload,
        )
    }

    canApprove(completedReportId) {
        const qs = CommonUtility.objectToParams({ completedReportId })
        return BaseService.get(`${APIPath.approvalCanApprove}?${qs}`)
    }

    getWorkflowConfig(templateId) {
        return BaseService.get(APIPath.approvalWorkflow(templateId))
    }

    saveWorkflowConfig(templateId, payload) {
        return BaseService.put(APIPath.approvalWorkflow(templateId), payload)
    }

    advanceReport(completedReportId, payload) {
        return BaseService.postExact(
            APIPath.approvalReportAdvance(completedReportId),
            payload,
        )
    }

    rejectReport(completedReportId, payload) {
        return BaseService.postExact(
            APIPath.approvalReportReject(completedReportId),
            payload,
        )
    }

    getReportHistory(completedReportId) {
        return BaseService.get(APIPath.approvalReportHistory(completedReportId))
    }

    getSignDetail(token) {
        return BaseService.get(APIPath.approvalSignDetail(token), false)
    }

    getSignHistory(token) {
        return BaseService.get(APIPath.approvalSignHistory(token), false)
    }

    // admins: e-mail the current approver a new approval link (the previous one stops working)
    resendApprovalLink(completedReportId) {
        return BaseService.postExact(
            APIPath.approvalReportResendLink(completedReportId),
            {},
        )
    }

    advanceSign(token, payload) {
        return BaseService.postExact(
            APIPath.approvalSignAdvance(token),
            payload,
            false,
        )
    }

    rejectSign(token, payload) {
        return BaseService.postExact(
            APIPath.approvalSignReject(token),
            payload,
            false,
        )
    }

    /**
     * Task list exposes AuditId/InspectionId, but Approval APIs need CompletedReportId.
     * Resolve via PendingReports by matching AuditId. Also returns approval status.
     */
    async resolveApprovalForAudit({
        completedReportId,
        auditId,
    } = {}) {
        let cacheKey = null
        if (completedReportId != null && completedReportId !== '') {
            cacheKey = `cr:${completedReportId}`
        } else if (auditId != null && auditId !== '') {
            cacheKey = `audit:${auditId}`
        }

        if (cacheKey && approvalLookupCache.has(cacheKey)) {
            return approvalLookupCache.get(cacheKey)
        }

        if (
            (completedReportId == null || completedReportId === '') &&
            (auditId == null || auditId === '')
        ) {
            return null
        }

        const targetAudit =
            auditId != null && auditId !== '' ? String(auditId) : null

        const findOnPage = (status, page) =>
            this.listPendingReports({ page, size: 100, status }).then(
                result => {
                    const normalized = normalizeApprovalListResponse(result)
                    const match = (normalized.items || []).find(item => {
                        if (
                            completedReportId != null &&
                            completedReportId !== '' &&
                            String(item.completedReportId) ===
                                String(completedReportId)
                        ) {
                            return true
                        }
                        return (
                            targetAudit != null &&
                            String(item.auditId) === targetAudit
                        )
                    })
                    if (match?.completedReportId != null) {
                        return {
                            completedReportId: match.completedReportId,
                            isApproved:
                                match.status === APPROVAL_STATUS.Approved,
                        }
                    }

                    const loaded = page * 100
                    const hasMore =
                        page < 5 &&
                        (normalized.items || []).length >= 100 &&
                        loaded < (normalized.totalCount || 0)

                    if (!hasMore) return null
                    return findOnPage(status, page + 1)
                },
            )

        let resolved = await findOnPage('pending', 1)
        if (resolved == null) {
            resolved = await findOnPage('approved', 1)
        }
        if (resolved == null) {
            resolved = await findOnPage('all', 1)
        }

        if (resolved && cacheKey) {
            approvalLookupCache.set(cacheKey, resolved)
            if (resolved.completedReportId != null) {
                approvalLookupCache.set(
                    `cr:${resolved.completedReportId}`,
                    resolved,
                )
            }
            if (targetAudit) {
                approvalLookupCache.set(`audit:${targetAudit}`, resolved)
            }
        }

        return resolved
    }

    async resolveCompletedReportId(params = {}) {
        const resolved = await this.resolveApprovalForAudit(params)
        return resolved?.completedReportId ?? null
    }

    /**
     * Bulk lookup for task list badges/menus: auditId → { isApproved, completedReportId }
     */
    async loadApprovalStatusesForAuditIds(auditIds = []) {
        const needed = Array.from(
            new Set(
                (auditIds || [])
                    .filter(id => id != null && id !== '')
                    .map(id => String(id)),
            ),
        )
        const map = {}
        if (!needed.length) return map

        needed.forEach(id => {
            const cached = approvalLookupCache.get(`audit:${id}`)
            if (cached) map[id] = cached
        })

        const missing = needed.filter(id => !map[id])
        if (!missing.length) return map

        const missingSet = new Set(missing)

        const ingestPage = (status, page) =>
            this.listPendingReports({ page, size: 100, status }).then(
                result => {
                    const normalized = normalizeApprovalListResponse(result)
                    const items = normalized.items || []
                    items.forEach(item => {
                        const auditKey =
                            item.auditId != null ? String(item.auditId) : null
                        if (!auditKey || !missingSet.has(auditKey)) return

                        const entry = {
                            completedReportId: item.completedReportId,
                            isApproved:
                                status === 'approved' ||
                                item.status === APPROVAL_STATUS.Approved,
                        }
                        map[auditKey] = entry
                        approvalLookupCache.set(`audit:${auditKey}`, entry)
                        if (entry.completedReportId != null) {
                            approvalLookupCache.set(
                                `cr:${entry.completedReportId}`,
                                entry,
                            )
                        }
                        missingSet.delete(auditKey)
                    })

                    if (!missingSet.size) return map

                    const loaded = page * 100
                    const hasMore =
                        page < 5 &&
                        (normalized.items || []).length >= 100 &&
                        loaded < (normalized.totalCount || 0)

                    if (!hasMore) return map
                    return ingestPage(status, page + 1)
                },
            )

        await ingestPage('approved', 1)
        if (missingSet.size) {
            await ingestPage('pending', 1)
        }

        return map
    }

    clearApprovalLookupCache() {
        approvalLookupCache.clear()
    }
}

export const ReportApprovalService = new ReportApprovalServiceClass()
